/**
 * TerminatedDealPanel — the full-width view of an ended Wisconsin deal
 * (design §4.1, §5.3; copy §6 and §8): a neutral banner, the facts the
 * platform recorded, the earnest-money panel and the termination record, and
 * for the seller the relist prompt. No celebration and no blame.
 *
 * Reads `contract_tracking.terminated` = `{at, reason, contingency_id,
 * event_id, notice_id, transition, wind_down_pending, termination_url,
 * record_url, banner_copy_key, relist_prompt}`. The server sends
 * `relist_prompt` to the seller only (`{copy_key, text, listing_id,
 * automatic: false}`; never an automatic relist), so the panel shows it
 * whenever it's there.
 *
 * Copy §15.2: an offer an admin recorded as ended by a document both parties
 * signed (`reason = admin_record_agreement`) has no contingency. The panel
 * says what was recorded (`WI.timeline.deal_ended_by_agreement`), that the
 * date is when cutthe6.com recorded it, and lists the deal-level timeline
 * entries (events with no `contingency_id`): the `admin_deal_record` event,
 * with the staff note (its `reason`) and the signed document
 * (`details.evidence`, which both parties can open through
 * `GET /api/contingency-attachments/{id}`). The `deal_terminated` event that
 * follows it says the same thing, so it isn't listed twice. The contingency
 * rows' own timelines can't show either.
 */

import React, { useId, useState } from 'react';
import { downloadContingencyAttachment, noticeErrorInfo } from '../../../agents/contingencyNoticesAgent';
import { contractCopy } from './contractCopy';
import {
  REASON_ADMIN_AGREEMENT,
  actorLabel,
  eventLabel,
  formatCentralInstant,
  noticeTitle,
  parseApiInstant,
} from './contractFormat';
import EarnestMoneyNeutralPanel from './EarnestMoneyNeutralPanel';
import styles from './TerminatedDealPanel.module.css';

/** Legal copy for a `{copy_key, text}` pair: the reviewed local string, else the server's. */
function copyOf(key, serverText) {
  return (key && contractCopy(key)) || serverText || null;
}

/** The document an admin recorded with a deal-level event (`details.evidence`), if any. */
const evidenceOf = (event) => {
  const evidence = event?.details?.evidence;
  return evidence && evidence.file_id != null ? evidence : null;
};

function EvidenceButton({ evidence }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const open = async () => {
    setLoading(true);
    setError(null);
    try {
      await downloadContingencyAttachment(evidence.file_id, evidence.filename || undefined);
    } catch (err) {
      setError(noticeErrorInfo(err).message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <span className={styles.evidence}>
      <button
        type="button"
        className={styles.linkBtn}
        onClick={open}
        disabled={loading}
        data-testid={`deal-evidence-${evidence.file_id}`}
      >
        {loading ? 'Opening…' : `Open the document: ${evidence.filename || 'signed document'}`}
      </button>
      {error && <span className={styles.inlineError} role="alert">{error}</span>}
    </span>
  );
}

/**
 * Deal-level timeline rows: events with no contingency, oldest first, without
 * dry runs, and without the `deal_terminated` event an `admin_deal_record`
 * caused (`details.admin_deal_record_event_id`), which would repeat it.
 */
export function dealTimelineEntries(events = []) {
  const rows = (events || []).filter((e) => e && e.contingency_id == null && !e.dry_run);
  const records = new Set(rows.filter((e) => e.event_type === 'admin_deal_record').map((e) => e.id));
  return rows
    .filter((e) => !(e.event_type === 'deal_terminated' && records.has(e.details?.admin_deal_record_event_id)))
    .sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')) || (a.id ?? 0) - (b.id ?? 0));
}

/** The staff note on a deal-level admin record (the event's `reason`). */
const staffNote = (event) => (
  event?.event_type === 'admin_deal_record' && typeof event.reason === 'string' && event.reason.trim()
    ? event.reason.trim() : null
);

export default function TerminatedDealPanel({ transaction, events = [] }) {
  const bannerId = `terminated-banner-${useId()}`;
  const timelineId = `deal-timeline-${useId()}`;
  const terminated = transaction?.contract_tracking?.terminated;
  if (!terminated) return null;
  const byAgreement = terminated.reason === REASON_ADMIN_AGREEMENT;
  const banner = copyOf(terminated.banner_copy_key, null) || contractCopy('WI.terminated.banner');
  const relist = terminated.relist_prompt
    ? copyOf(terminated.relist_prompt.copy_key, terminated.relist_prompt.text)
    : null;
  const row = (transaction.contingencies || []).find((c) => c.id === terminated.contingency_id);
  const contract = row?.contract;
  const notice = (contract?.notices || []).find((n) => n.id === terminated.notice_id);
  const endedAt = formatCentralInstant(terminated.at);
  const dealRows = dealTimelineEntries(events);

  return (
    <section
      className={styles.panel}
      aria-labelledby={bannerId}
      data-testid="terminated-deal-panel"
      data-reason={terminated.reason || undefined}
    >
      <div className={styles.banner} role="status">
        <p id={bannerId} className={styles.bannerText}>{banner}</p>
        {byAgreement && (
          <p className={styles.bannerSub} data-testid="terminated-by-agreement">
            {contractCopy('WI.timeline.deal_ended_by_agreement')}
          </p>
        )}
      </div>

      <dl className={styles.facts} data-testid="termination-facts">
        {byAgreement && (
          <div className={styles.fact}>
            <dt>What was recorded</dt>
            <dd>{contractCopy('WI.timeline.deal_ended_by_agreement')}</dd>
          </div>
        )}
        {contract?.heading && (
          <div className={styles.fact}>
            <dt>Contingency</dt>
            <dd>{contract.heading}</dd>
          </div>
        )}
        {notice && (
          <div className={styles.fact}>
            <dt>Notice</dt>
            <dd>{noticeTitle(notice.notice_type)}</dd>
          </div>
        )}
        {endedAt && (
          <div className={styles.fact}>
            <dt>Recorded on</dt>
            <dd>{endedAt}</dd>
          </div>
        )}
      </dl>

      {dealRows.length > 0 && (
        <section className={styles.timeline} aria-labelledby={timelineId} data-testid="deal-timeline">
          <h3 id={timelineId} className={styles.timelineHeading}>Transaction timeline</h3>
          <ol className={styles.timelineList}>
            {dealRows.map((e) => {
              const evidence = evidenceOf(e);
              return (
                <li key={e.id} className={styles.timelineItem} data-testid="deal-timeline-item">
                  <p className={styles.timelineLabel}>{eventLabel(e, null, null)}</p>
                  <p className={styles.timelineMeta}>
                    {actorLabel(e.actor_type) && <span>{actorLabel(e.actor_type)}</span>}
                    {e.at && (
                      <time dateTime={parseApiInstant(e.at)?.toISOString()}>{formatCentralInstant(e.at)}</time>
                    )}
                  </p>
                  {staffNote(e) && (
                    <p className={styles.timelineNote}>{`Staff note: ${staffNote(e)}`}</p>
                  )}
                  {evidence && <EvidenceButton evidence={evidence} />}
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {relist && (
        <p className={styles.relist} data-testid="relist-prompt">{relist}</p>
      )}

      <EarnestMoneyNeutralPanel transactionId={transaction.id} terminationReason={terminated.reason} />
    </section>
  );
}
